import React, { useEffect, useState } from 'react';
import { supabase } from '../../../helpers/Supabase';
import Loader from '../../Loader/Loader';
import DelegateManagement from './DelegateManagement';
import '../../../styles/components/Settings.scss';

interface Client {
  id: string;
  name: string;
  location: string;
  logo_url: string;
  industry: string;
  created_at: string;
}

interface Store {
  id: string;
  store_name: string;
  zip: string;
  is_active: boolean;
  hash_id: string;
  client_id: string;
  industry?: string;
  created_at: string;
}

interface User {
  id: string;
  tier: 'free' | 'monthly' | 'annual';
  stripe_customer_id?: string;
  created_at: string;
}

interface PricingTier {
  name: string;
  price: number;
  period: string;
  storeLimit: number;
  features: string[];
  savings?: string;
}

const Settings: React.FC = () => {
  const [client, setClient] = useState<Client | null>(null);
  const [stores, setStores] = useState<Store[]>([]);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showPricingAlert, setShowPricingAlert] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    location: '',
    industry: ''
  });
  const [newLogo, setNewLogo] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [industryOptions, setIndustryOptions] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const pricingTiers: Record<string, PricingTier> = {
    free: {
      name: 'Free Trial',
      price: 0,
      period: 'forever',
      storeLimit: 1,
      features: ['Basic analytics', '1 location', 'Core features']
    },
    monthly: {
      name: 'Monthly Pro',
      price: 60,
      period: '/store/month',
      storeLimit: 999, // Unlimited
      features: ['Unlimited locations', 'AI chatbot access', 'Advanced analytics', 'Priority support']
    },
    annual: {
      name: 'Annual Pro',
      price: 400,
      period: '/store/year',
      storeLimit: 999, // Unlimited
      features: ['Everything in Monthly', '44% savings', 'Early access features', 'VIP support'],
      savings: 'Save 44%'
    }
  };

  useEffect(() => {
    fetchClientData();
    fetchIndustryOptions();
  }, []);

  const fetchClientData = async () => {
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) return;

      const { data: userRows } = await supabase
        .from('users')
        .select('*')
        .eq('auth_user_id', authUser.id)
        .order('created_at', { ascending: false })
        .limit(1);

      const userRow = userRows && userRows.length > 0 ? userRows[0] : null;

      if (userRow) {
        setUser(userRow);

        // Fetch client info
        const { data: clientData } = await supabase
          .from('clients')
          .select('*')
          .eq('id', userRow.client_id)
          .single();

        if (clientData) {
          setClient(clientData);
          setEditForm({
            name: clientData.name || '',
            location: clientData.location || '',
            industry: clientData.industry || ''
          });
        }

        // Fetch stores
        const { data: storesData } = await supabase
          .from('stores')
          .select('*')
          .eq('client_id', userRow.client_id)
          .order('created_at', { ascending: false });

        setStores(storesData || []);
      }
    } catch (error) {
      console.error('Error fetching client data:', error);
      setError('Failed to load client data');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchIndustryOptions = async () => {
    try {
      const { data } = await supabase
        .from('industry_templates')
        .select('industry')
        .neq('industry', '')
        .order('industry', { ascending: true });
      
      if (data) {
        const industrySet = new Set<string>();
        data.forEach(d => industrySet.add(d.industry));
        const uniqueIndustries = Array.from(industrySet);
        setIndustryOptions(uniqueIndustries);
      }
    } catch (error) {
      console.error('Error fetching industries:', error);
    }
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      const file = e.target.files[0];
      console.log('📁 File selected:', {
        name: file.name,
        size: file.size,
        type: file.type,
        lastModified: file.lastModified
      });
      
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file (PNG, JPG, GIF, etc.)');
        return;
      }
      
      // Validate file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        setError('File is too large. Please select a file smaller than 10MB.');
        return;
      }
      
      setNewLogo(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveChanges = async () => {
    if (!client) return;
    
    setError('');
    setSuccess('');
    
    try {
      let logoUrl = client.logo_url;

      // Upload new logo if selected
      if (newLogo) {
        console.log('📤 Starting logo upload...', newLogo.name, newLogo.size, newLogo.type);
        
        // Validate file
        if (newLogo.size > 10 * 1024 * 1024) { // 10MB limit
          throw new Error('Logo file is too large. Please use a file smaller than 10MB.');
        }
        
        if (!newLogo.type.startsWith('image/')) {
          throw new Error('Please select a valid image file.');
        }
        
        const cleanFileName = newLogo.name.replace(/\s+/g, '_').toLowerCase();
        const filePath = `${Date.now()}-${cleanFileName}`;
        
        console.log('📁 Uploading to path:', filePath);
        console.log('📁 File object details:', {
          name: newLogo.name,
          size: newLogo.size,
          type: newLogo.type,
          lastModified: newLogo.lastModified,
          constructor: newLogo.constructor.name,
          isFile: newLogo instanceof File,
          isBlob: newLogo instanceof Blob
        });
        
        // Upload the file using direct fetch to avoid form data wrapping
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) {
          throw new Error('No authentication token available');
        }

        const uploadUrl = `${process.env.REACT_APP_SUPABASE_URL}/storage/v1/object/client-logos/${filePath}`;
        
        const response = await fetch(uploadUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': newLogo.type || 'image/png',
            'Cache-Control': '3600',
            'x-upsert': 'false'
          },
          body: newLogo
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Upload failed: ${response.status} ${response.statusText} - ${errorText}`);
        }

        const uploadData = await response.json();
        console.log('✅ Direct upload successful:', uploadData);
        
        console.log('✅ Upload successful:', uploadData);
        // Use the direct path for the correct URL
        logoUrl = `${process.env.REACT_APP_SUPABASE_URL}/storage/v1/object/public/client-logos/${filePath}`;
        console.log('🔗 Logo URL:', logoUrl);
        console.log('🔗 Environment URL:', process.env.REACT_APP_SUPABASE_URL);
        console.log('🔗 File path:', filePath);
        
        // Test if the file is accessible
        try {
          const response = await fetch(logoUrl);
          console.log('🔍 File accessibility test:', {
            status: response.status,
            contentType: response.headers.get('content-type'),
            contentLength: response.headers.get('content-length')
          });
          
          if (!response.ok) {
            throw new Error(`File not accessible: ${response.status} ${response.statusText}`);
          }
        } catch (error) {
          console.error('❌ File accessibility test failed:', error);
          throw new Error('Uploaded file is not accessible. Please try again.');
        }
      }

      console.log('💾 Saving to database with logo URL:', logoUrl);
      
      // Update client info using upsert to avoid CORS PATCH issues
      const { data: updateData, error: updateError } = await supabase
        .from('clients')
        .upsert({
          id: client.id,
          name: editForm.name,
          location: editForm.location,
          industry: editForm.industry,
          logo_url: logoUrl
        }, {
          onConflict: 'id'
        });

      if (updateError) {
        console.error('❌ Database update error:', updateError);
        throw updateError;
      }
      
      console.log('✅ Database update successful:', updateData);

      // Update local state
      setClient({
        ...client,
        name: editForm.name,
        location: editForm.location,
        industry: editForm.industry,
        logo_url: logoUrl
      });

      setSuccess('Settings updated successfully!');
      setIsEditing(false);
      setNewLogo(null);
      setLogoPreview(null);

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (error: any) {
      console.error('Error updating settings:', error);
      setError(error.message || 'Failed to update settings');
    }
  };

  const getCurrentTier = (): PricingTier => {
    if (!user || !user.tier || !pricingTiers[user.tier]) {
      return pricingTiers.free;
    }
    return pricingTiers[user.tier];
  };

  const calculateMonthlyCost = () => {
    const currentTier = getCurrentTier();
    const activeStores = stores.filter(s => s.is_active).length;
    
    if (currentTier.storeLimit === 999) {
      return activeStores * currentTier.price;
    }
    
    return Math.max(activeStores, currentTier.storeLimit) * currentTier.price;
  };

  const toggleStoreStatus = async (storeId: string, currentStatus: boolean) => {
    if (!user) return;

    const currentTier = getCurrentTier();
    const activeStores = stores.filter(s => s.is_active).length;

    // If trying to activate and would exceed limit
    if (!currentStatus && activeStores >= currentTier.storeLimit && currentTier.storeLimit !== 999) {
      setShowPricingAlert(true);
      return;
    }

    try {
      // Get the current store data to include all required fields
      const currentStore = stores.find(s => s.id === storeId);
      if (!currentStore) {
        setError('Store not found');
        return;
      }

      const { error } = await supabase
        .from('stores')
        .upsert({
          id: storeId,
          store_name: currentStore.store_name,
          client_id: currentStore.client_id,
          hash_id: currentStore.hash_id,
          industry: currentStore.industry,
          is_active: !currentStatus
        }, {
          onConflict: 'id'
        });

      if (!error) {
        setStores(prev => prev.map(store => 
          store.id === storeId 
            ? { ...store, is_active: !currentStatus }
            : store
        ));
        setSuccess(`Store ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
      } else {
        setError('Failed to update store status');
      }
    } catch (error) {
      console.error('Error updating store status:', error);
      setError('Failed to update store status');
    }
  };

  if (isLoading) return <Loader />;

  if (!client) {
    return (
      <div className="settings-container">
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          Failed to load client data
        </div>
      </div>
    );
  }

  return (
    <div className="settings-container">
      {/* Header */}
      <div className="settings-header">
        <h1>⚙️ Account Settings</h1>
        <p>Manage your business profile and store locations</p>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="success-message">
          <span className="success-icon">✅</span>
          {success}
        </div>
      )}
      
      {error && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          {error}
        </div>
      )}

      <div className="settings-grid">
        {/* Business Profile Section */}
        <div className="settings-section">
          <div className="section-header">
            <h2>🏢 Business Profile</h2>
            {!isEditing && (
              <button 
                onClick={() => setIsEditing(true)}
                className="edit-btn"
              >
                ✏️ Edit
              </button>
            )}
          </div>

          {isEditing ? (
            <div className="edit-form">
              <div className="form-group">
                <label htmlFor="business-name">Business Name</label>
                <input
                  id="business-name"
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  placeholder="Enter business name"
                  className="modern-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="business-location">Location</label>
                <input
                  id="business-location"
                  type="text"
                  value={editForm.location}
                  onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                  placeholder="Enter business location"
                  className="modern-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="business-industry">Industry</label>
                <select
                  id="business-industry"
                  value={editForm.industry}
                  onChange={(e) => setEditForm({ ...editForm, industry: e.target.value })}
                  className="modern-select"
                >
                  <option value="">Select industry...</option>
                  {industryOptions.map(industry => (
                    <option key={industry} value={industry}>{industry}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="business-logo">Business Logo</label>
                <div className="logo-upload-section">
                  {(logoPreview || client.logo_url) && (
                    <div className="logo-preview">
                      <img 
                        src={logoPreview || client.logo_url} 
                        alt="Business Logo" 
                      />
                    </div>
                  )}
                  <input
                    id="business-logo"
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="logo-input"
                  />
                  <label htmlFor="business-logo" className="upload-label">
                    {logoPreview || client.logo_url ? 'Change Logo' : 'Upload Logo'}
                  </label>
                </div>
              </div>

              <div className="form-actions">
                <button 
                  onClick={handleSaveChanges}
                  className="save-btn"
                >
                  💾 Save Changes
                </button>
                <button 
                  onClick={() => {
                    setIsEditing(false);
                    setEditForm({
                      name: client.name || '',
                      location: client.location || '',
                      industry: client.industry || ''
                    });
                    setNewLogo(null);
                    setLogoPreview(null);
                  }}
                  className="cancel-btn"
                >
                  ❌ Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="profile-display">
              <div className="profile-info">
                <div className="info-row">
                  <span className="label">Business Name:</span>
                  <span className="value">{client?.name || 'Not set'}</span>
                </div>
                <div className="info-row">
                  <span className="label">Location:</span>
                  <span className="value">{client?.location || 'Not set'}</span>
                </div>
                <div className="info-row">
                  <span className="label">Industry:</span>
                  <span className="value">{client?.industry || 'Not set'}</span>
                </div>
                <div className="info-row">
                  <span className="label">Member Since:</span>
                  <span className="value">
                    {client?.created_at ? new Date(client.created_at).toLocaleDateString() : 'Not set'}
                  </span>
                </div>
              </div>
              {client?.logo_url && (
                <div className="logo-display">
                  <img src={client.logo_url} alt="Business Logo" />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Billing & Subscription Section */}
        <div className="settings-section">
          <div className="section-header">
            <h2>💳 Billing & Subscription</h2>
            <button 
              onClick={() => setShowPaymentModal(true)}
              className="edit-btn"
            >
              🔄 Manage
            </button>
          </div>

          <div className="billing-info">
            <div className="current-plan">
              <div className="plan-header">
                <h3>{getCurrentTier().name}</h3>
                <span className={`plan-badge ${user?.tier}`}>
                  {user?.tier === 'free' ? 'Free' : user?.tier === 'monthly' ? 'Monthly' : 'Annual'}
                </span>
              </div>
              
              <div className="plan-details">
                <div className="price-info">
                  <span className="price">
                    ${getCurrentTier().price === 0 ? '0' : getCurrentTier().price}
                  </span>
                  <span className="period">{getCurrentTier().period}</span>
                  {getCurrentTier().savings && (
                    <span className="savings">{getCurrentTier().savings}</span>
                  )}
                </div>
                
                <div className="usage-info">
                  <div className="usage-item">
                    <span className="label">Active Stores:</span>
                    <span className="value">
                      {stores.filter(s => s.is_active).length} / {getCurrentTier().storeLimit === 999 ? '∞' : getCurrentTier().storeLimit}
                    </span>
                  </div>
                  <div className="usage-item">
                    <span className="label">{user?.tier === 'annual' ? 'Annual Cost:' : 'Monthly Cost:'}</span>
                    <span className="value">${calculateMonthlyCost().toFixed(2)}</span>
                  </div>
                </div>

                <div className="plan-features">
                  {getCurrentTier().features.map((feature, index) => (
                    <div key={index} className="feature">
                      ✓ {feature}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {user?.tier !== 'free' && (
              <div className="payment-info">
                <h4>Payment Method</h4>
                {user?.stripe_customer_id ? (
                  <div className="payment-method">
                    <span className="payment-icon">💳</span>
                    <span>Card ending in ****1234</span>
                    <button className="update-payment-btn">Update</button>
                  </div>
                ) : (
                  <div className="no-payment">
                    <span>No payment method on file</span>
                    <button className="add-payment-btn">Add Payment Method</button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Delegate Management Section */}
        <div className="settings-section full-width">
          <DelegateManagement clientId={client.id} stores={stores} />
        </div>

        {/* Store Management Section */}
        <div className="settings-section full-width">
          <div className="section-header">
            <h2>🏪 Store Management</h2>
            <div className="store-header-actions">
              <span className="store-count">{stores.length} stores</span>
              {stores.filter(s => s.is_active).length >= getCurrentTier().storeLimit && getCurrentTier().storeLimit !== 999 && (
                <span className="limit-warning">⚠️ Store limit reached</span>
              )}
            </div>
          </div>

          <div className="stores-list">
            {stores.length === 0 ? (
              <div className="no-stores">
                <p>No stores found. Add your first store to get started!</p>
              </div>
            ) : (
              stores.map(store => (
                <div 
                  key={store.id} 
                  className={`store-item ${!store.is_active ? 'inactive' : ''}`}
                >
                  <div className="store-info">
                    <h3>{store.store_name}</h3>
                    <p>Zip: {store.zip || 'N/A'}</p>
                    <p>Created: {new Date(store.created_at).toLocaleDateString()}</p>
                    <a 
                      href={`/checkin/${store.hash_id}`} 
                      target="_blank" 
                      rel="noreferrer"
                      className="checkin-link"
                    >
                      🔗 View Check-In Page
                    </a>
                  </div>
                  <div className="store-actions">
                    <button
                      onClick={() => toggleStoreStatus(store.id, store.is_active)}
                      className={`status-btn ${store.is_active ? 'deactivate' : 'activate'}`}
                      disabled={!store.is_active && stores.filter(s => s.is_active).length >= getCurrentTier().storeLimit && getCurrentTier().storeLimit !== 999}
                    >
                      {store.is_active ? '🗑️ Deactivate' : '♻️ Activate'}
                    </button>
                    <span className={`status-badge ${store.is_active ? 'active' : 'inactive'}`}>
                      {store.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Pricing Alert Modal */}
      {showPricingAlert && (
        <div className="modal-overlay">
          <div className="modal-content pricing-alert">
            <div className="alert-header">
              <h3>🚀 Upgrade Your Plan</h3>
              <button 
                onClick={() => setShowPricingAlert(false)}
                className="close-btn"
              >
                ✕
              </button>
            </div>
            <div className="alert-body">
              <p>You've reached your store limit on the {getCurrentTier().name} plan!</p>
              <p>Upgrade to add more stores and unlock additional features.</p>
              
              <div className="upgrade-options">
                <div className="upgrade-option">
                  <h4>Monthly Pro</h4>
                  <div className="price">$60/store/month</div>
                  <button className="upgrade-btn">Upgrade Now</button>
                </div>
                <div className="upgrade-option featured">
                  <h4>Annual Pro</h4>
                  <div className="price">$400/store/year</div>
                  <div className="savings">Save 44%</div>
                  <button className="upgrade-btn">Upgrade Now</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Management Modal */}
      {showPaymentModal && (
        <div className="modal-overlay">
          <div className="modal-content payment-modal">
            <div className="modal-header">
              <h3>💳 Payment Management</h3>
              <button 
                onClick={() => setShowPaymentModal(false)}
                className="close-btn"
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="payment-section">
                <h4>Current Payment Method</h4>
                {user?.stripe_customer_id ? (
                  <div className="current-payment">
                    <div className="card-info">
                      <span className="card-icon">💳</span>
                      <div className="card-details">
                        <span>Visa ending in 1234</span>
                        <span>Expires 12/25</span>
                      </div>
                    </div>
                    <button className="update-card-btn">Update Card</button>
                  </div>
                ) : (
                  <div className="no-payment-method">
                    <p>No payment method on file</p>
                    <button className="add-card-btn">Add Payment Method</button>
                  </div>
                )}
              </div>

              <div className="billing-section">
                <h4>Billing History</h4>
                <div className="billing-history">
                  <div className="billing-item">
                    <div className="billing-info">
                      <span className="billing-date">Dec 1, 2024</span>
                      <span className="billing-amount">$120.00</span>
                    </div>
                    <span className="billing-status paid">Paid</span>
                  </div>
                  <div className="billing-item">
                    <div className="billing-info">
                      <span className="billing-date">Nov 1, 2024</span>
                      <span className="billing-amount">$120.00</span>
                    </div>
                    <span className="billing-status paid">Paid</span>
                  </div>
                </div>
              </div>

              <div className="plan-section">
                <h4>Current Plan</h4>
                <div className="plan-summary">
                  <div className="plan-info">
                    <span className="plan-name">{getCurrentTier().name}</span>
                    <span className="plan-price">${getCurrentTier().price}{getCurrentTier().period}</span>
                  </div>
                  <button className="change-plan-btn">Change Plan</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;